import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Filter, ChevronLeft, Save, Trash2 } from 'lucide-react';
import { TeamCard } from '../components/TeamCard';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { DistributionPreview } from '../components/DistributionPreview';
import { MOCK_TEAMS } from '../data/mockData';
import { Team, User } from '../types';
export function TeamsPage() {
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teams, setTeams] = useState<Team[]>(MOCK_TEAMS);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredTeams = teams.filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()) || t.campaignName.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleTeamClick = (team: Team) => {
    setSelectedTeam(team);
  };
  const handleBack = () => {
    setSelectedTeam(null);
  };
  // Animation variants
  const pageVariants = {
    initial: {
      opacity: 0,
      x: 20
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -20
    }
  };
  if (selectedTeam) {
    return <div className="p-8 max-w-7xl mx-auto space-y-8" data-id="element-273">
        <div className="flex items-center justify-between" data-id="element-274">
          <div className="flex items-center space-x-4" data-id="element-275">
            <Button variant="ghost" size="sm" onClick={handleBack} leftIcon={<ChevronLeft className="w-4 h-4" data-id="element-277" />} data-id="element-276">
              Back to Teams
            </Button>
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-278">
              {selectedTeam.name}
            </h1>
            <Badge variant={selectedTeam.active ? 'success' : 'neutral'} data-id="element-279">
              {selectedTeam.active ? 'Active' : 'Paused'}
            </Badge>
          </div>
          <div className="flex space-x-3" data-id="element-280">
            <Button variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200" data-id="element-281">
              Delete Team
            </Button>
            <Button leftIcon={<Save className="w-4 h-4" data-id="element-283" />} data-id="element-282">
              Save Changes
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-284">
          {/* Team Settings */}
          <div className="space-y-6" data-id="element-285">
            <Card data-id="element-286">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-287">
                Configuration
              </h2>
              <div className="space-y-4" data-id="element-288">
                <Input label="Team Name" defaultValue={selectedTeam.name} data-id="element-289" />
                <Input label="Bonzo Campaign Name" defaultValue={selectedTeam.campaignName} data-id="element-290" />
                <Input label="Campaign ID" defaultValue={selectedTeam.campaignId} className="font-mono text-xs" data-id="element-291" />

                <div className="pt-4 border-t border-gray-100" data-id="element-292">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-293">
                    Distribution Method
                  </label>
                  <div className="flex space-x-4" data-id="element-294">
                    <label className="flex items-center space-x-2 cursor-pointer" data-id="element-295">
                      <input type="radio" name="distMethod" defaultChecked={selectedTeam.distributionMethod === 'round-robin'} className="text-indigo-600 focus:ring-indigo-500" data-id="element-296" />
                      <span className="text-sm text-gray-700" data-id="element-297">Round Robin</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer" data-id="element-298">
                      <input type="radio" name="distMethod" defaultChecked={selectedTeam.distributionMethod === 'weighted'} className="text-indigo-600 focus:ring-indigo-500" data-id="element-299" />
                      <span className="text-sm text-gray-700" data-id="element-300">Weighted</span>
                    </label>
                  </div>
                </div>
              </div>
            </Card>

            <Card data-id="element-301">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-302">
                Distribution Preview
              </h2>
              <DistributionPreview users={selectedTeam.users} distributionMethod={selectedTeam.distributionMethod} data-id="element-303" />
            </Card>
          </div>

          {/* Users List */}
          <div className="lg:col-span-2 space-y-6" data-id="element-304">
            <div className="flex items-center justify-between" data-id="element-305">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-306">
                Team Members
              </h2>
              <Button size="sm" variant="secondary" leftIcon={<Plus className="w-4 h-4" data-id="element-308" />} data-id="element-307">
                Add User
              </Button>
            </div>

            <Card noPadding data-id="element-309">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-310">
                <thead className="bg-gray-50" data-id="element-311">
                  <tr data-id="element-312">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-313">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-314">
                      Bonzo ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-315">
                      Weight
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-316">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-317">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-318">
                  {selectedTeam.users.map(user => <tr key={user.id} className="hover:bg-gray-50" data-id="element-319">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-320">
                        <div className="flex items-center" data-id="element-321">
                          <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs" data-id="element-322">
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div className="ml-4" data-id="element-323">
                            <div className="text-sm font-medium text-gray-900" data-id="element-324">
                              {user.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-325">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono" data-id="element-326">
                        {user.bonzoOwnerId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-327">
                        {selectedTeam.distributionMethod === 'weighted' ? <div className="flex items-center space-x-2" data-id="element-328">
                            <input type="range" min="0" max="100" defaultValue={user.weight} className="w-24 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" data-id="element-329" />
                            <span className="font-medium" data-id="element-330">{user.weight}%</span>
                          </div> : <span className="text-gray-400 italic" data-id="element-331">Equal</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-332">
                        <Badge variant={user.active ? 'success' : 'neutral'} data-id="element-333">
                          {user.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-334">
                        <button className="text-gray-400 hover:text-red-600 transition-colors" data-id="element-335">
                          <Trash2 className="w-4 h-4" data-id="element-336" />
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </Card>
          </div>
        </div>
      </div>;
  }
  return <div className="p-8 max-w-7xl mx-auto space-y-8" data-id="element-337">
      <div className="flex items-center justify-between" data-id="element-338">
        <div data-id="element-339">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-340">Teams</h1>
          <p className="text-gray-500 mt-1" data-id="element-341">
            Manage distribution rules and team members
          </p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" data-id="element-343" />} data-id="element-342">Create Team</Button>
      </div>

      <div className="flex items-center space-x-4 bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-344">
        <div className="relative flex-1" data-id="element-345">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" data-id="element-346" />
          <input type="text" placeholder="Search teams or campaigns..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-347" />
        </div>
        <Button variant="secondary" leftIcon={<Filter className="w-4 h-4" data-id="element-349" />} data-id="element-348">
          Filter
        </Button>
      </div>

      <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" animate="show" variants={{
      hidden: {
        opacity: 0
      },
      show: {
        opacity: 1,
        transition: {
          staggerChildren: 0.1
        }
      }
    }} data-id="element-350">
        {filteredTeams.map(team => <motion.div key={team.id} variants={{
        hidden: {
          opacity: 0,
          y: 20
        },
        show: {
          opacity: 1,
          y: 0
        }
      }} data-id="element-351">
            <TeamCard team={team} onClick={() => handleTeamClick(team)} data-id="element-352" />
          </motion.div>)}
      </motion.div>
    </div>;
}